import { cva, type VariantProps } from 'class-variance-authority';
import {
  disabledState,
  focusRing,
  transitionBase,
  uiMarker,
  uiMarkerLayout,
} from './shared';

/**
 * LISTS AND ROWS
 *
 * Four shapes the product built out of ul, li and div, and the difference
 * between them is the whole reason they are separate recipes:
 *
 *   List / ListItem   words in a list — plan features with a tick, the steps
 *                     of an instruction, the bullets under a setting.
 *   KeyValueRow       a label on the left and its value on the right. Specs,
 *                     invoice totals, fee tables.
 *   ListRow           one row of a list of *things* — an invoice, a document,
 *                     a vehicle — with a thumbnail, two lines and a trailing
 *                     control, usually clickable.
 *   IconFact          a tinted icon tile beside a label-over-value pair. The
 *                     integration overviews and the job sheet draw this.
 */

export const listRecipe = cva([uiMarker, uiMarkerLayout, 'm-0 flex flex-col'], {
  variants: {
    marker: {
      none: 'list-none p-0',
      bullet: 'list-disc pl-5',
      number: 'list-decimal pl-5',
      /** A tick per item, drawn by ListItem. */
      check: 'list-none p-0',
    },
    gap: { 1: 'gap-1', 2: 'gap-2', 3: 'gap-3' },
    size: {
      sm: 'font-sans text-body-sm',
      md: 'font-sans text-body',
    },
    tone: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      /**
       * On a filled primary panel: softened cream text, and the ticks of a
       * `check` list follow it, since primary ticks on primary would vanish.
       */
      'on-primary': 'text-primary-foreground/80 [&_svg]:text-current',
    },
  },
  defaultVariants: { marker: 'bullet', gap: 2, size: 'sm', tone: 'default' },
});

/**
 * `list-item` display is what draws a bullet or a number, so a marked item
 * must stay one. Only the check variant lays out as a row, because it draws
 * its own glyph.
 */
export const listItemRecipe = cva([uiMarker], {
  variants: {
    marker: {
      none: '',
      bullet: '',
      number: '',
      check: 'flex items-start gap-2',
    },
    /**
     * One item's colour, over the list's. For a checklist whose items are
     * each met or not yet — the password rules on sign-up, where a rule
     * turns green as it is satisfied while the rest stay muted.
     */
    tone: {
      inherit: '',
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      success: 'text-success',
      destructive: 'text-destructive',
    },
  },
  defaultVariants: { marker: 'bullet', tone: 'inherit' },
});

/** The tick. Sits on the first line's baseline rather than centred on a wrap. */
export const listCheckRecipe = cva([uiMarker, 'mt-0.5 shrink-0 text-primary']);

export const keyValueRowRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-start justify-between gap-4'],
  {
    variants: {
      density: {
        compact: 'py-1.5',
        default: 'py-2',
        relaxed: 'py-3',
      },
      /** Horizontal inset, for rows inside a bordered box. */
      inset: { none: '', sm: 'px-3', md: 'px-4' },
      /** The total row: tinted so it reads as the answer. */
      highlight: { true: 'bg-muted', false: '' },
    },
    defaultVariants: { density: 'default', inset: 'none', highlight: false },
  }
);

export const listRowRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex w-full items-center gap-3 text-left',
    transitionBase,
  ],
  {
    variants: {
      inset: {
        sm: 'px-3 py-2.5',
        md: 'px-4 py-3',
        /**
         * No side inset: a row inside a card that already carries one, ruled
         * off from its neighbours by the Stack around it. 8px above and below.
         */
        y: 'py-2',
        /**
         * The same, except the first row starts flush with the top and the
         * last ends flush with the bottom, so the card's own padding is not
         * doubled at either end.
         */
        between: 'py-2 first:pt-0 last:pb-0',
      },
      /**
       * `band`: a row of the grouped list in a 400 column — 44 high, inset 16
       * from each edge so the hovered and selected row reads as a rounded
       * band, as the design draws it. Its columns carry their own widths, so
       * it has no gap of its own. `h-11` rather than a control size because
       * it is a list row, not a control that has to line up with an Input.
       */
      shape: {
        flush: '',
        band: 'mx-4 h-11 w-[calc(100%-2rem)] gap-0 rounded-lg border border-transparent px-2 tracking-tight',
      },
      /**
       * Where a dragged row would land: a rule along the top or bottom edge.
       * An inset shadow rather than a border or a spacer, so the line appears
       * without moving anything — the rows differ in height and a list that
       * shifts under the cursor jitters.
       */
      dropTarget: {
        none: '',
        above: 'shadow-[inset_0_2px_0_0_hsl(var(--primary))]',
        below: 'shadow-[inset_0_-2px_0_0_hsl(var(--primary))]',
      },
      interactive: {
        true: [
          'cursor-pointer hover:bg-muted/50',
          focusRing,
          disabledState,
          'aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
        ].join(' '),
        false: '',
      },
      /**
       * The row whose record is open beside the list — the impound picked in
       * the master-detail. The same tint TableRow uses for the same state,
       * so a list and a table that sit in the same product agree on what
       * "this one" looks like.
       */
      selected: {
        true: 'bg-info-subtle/60',
        false: '',
      },
      /**
       * Let the trailing controls drop under the text on a phone, rather
       * than squeezing the name. The domain rows carry a badge, a price and
       * a button; on a narrow screen that is a second line.
       */
      wrap: { true: 'flex-wrap', false: '' },
    },
    compoundVariants: [
      // A band is selected by taking a rule and the muted fill, not the
      // table's tint: it sits on the page rather than in a table.
      { shape: 'band', selected: true, class: 'border-border bg-muted' },
    ],
    defaultVariants: {
      inset: 'md',
      shape: 'flush',
      dropTarget: 'none',
      interactive: false,
      selected: false,
      wrap: false,
    },
  }
);

/**
 * THE GROUPED LIST COLUMN
 *
 * The 400-wide column beside a record on the inspections and trucks screens:
 * a toolbar, then a tinted band over each group of rows, the rows (ListRow
 * `band`), and a strip under them for a pager or a link. Every piece carries
 * the column's 16px inset itself, so the column has no padding of its own —
 * which is what lets a selected row colour the full width.
 */

/** The band's outer spacing: 12 above, which is the only gap between groups, and 8 below. */
export const listBandRecipe = cva([uiMarker, uiMarkerLayout, 'px-4 pb-2 pt-3']);

/**
 * The band itself. 41 high so the label sits on the row grid. `tone` follows
 * the badge tone of what the group is — a red band over red badges — and a
 * group that is not a severity (a truck type, a driver) is `neutral`.
 */
export const listBandBarRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex h-[41px] shrink-0 items-center gap-1 rounded-lg px-2 text-foreground',
  ],
  {
    variants: {
      tone: {
        neutral: 'bg-muted',
        success: 'bg-success-subtle',
        warning: 'bg-warning-subtle',
        info: 'bg-info-subtle',
        destructive: 'bg-destructive-subtle',
      },
    },
    defaultVariants: { tone: 'neutral' },
  }
);

/** The count. Half strength, or a lining digit reads as bigger than the lowercase word beside it. */
export const listBandCountRecipe = cva([uiMarker, 'shrink-0 opacity-50']);

/** A control at the far end of the band. */
export const listBandActionRecipe = cva([uiMarker, 'ml-auto shrink-0']);

/**
 * The strip under the rows, at the column's inset: a pager, or a link that
 * reveals more rows. `compact` for a single link.
 */
export const listFootRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex flex-wrap items-center justify-between gap-3 px-4',
  ],
  {
    variants: {
      density: { default: 'py-3', compact: 'py-2' },
    },
    defaultVariants: { density: 'default' },
  }
);
export type ListFootRecipeProps = VariantProps<typeof listFootRecipe>;

/**
 * The row above a grouped list: the view switch and the page's own buttons on
 * the left, the magnifier on the right, all at the column's inset.
 */
export const listToolbarRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-wrap items-center justify-between gap-2 px-4 pt-3',
]);

/** The search box the magnifier reveals, on its own line under the toolbar. */
export const listToolbarSearchRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'px-4 pt-3',
]);

export const iconFactRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-start gap-3'],
  {
    variants: {
      boxed: {
        true: 'rounded-lg border border-border bg-muted/50 p-4',
        false: '',
      },
    },
    defaultVariants: { boxed: false },
  }
);

export type ListRecipeProps = VariantProps<typeof listRecipe>;
export type ListItemRecipeProps = VariantProps<typeof listItemRecipe>;
export type KeyValueRowRecipeProps = VariantProps<typeof keyValueRowRecipe>;
export type ListRowRecipeProps = VariantProps<typeof listRowRecipe>;
export type ListBandBarRecipeProps = VariantProps<typeof listBandBarRecipe>;
export type IconFactRecipeProps = VariantProps<typeof iconFactRecipe>;
